import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { message } from 'antd';
import { useAuth } from '../context/AuthContext';
import { plansApi } from '../api/plans';
import DashboardLayout from '../components/DashboardLayout';
import PlanControls from '../components/plans/PlanControls';
import PlanContent from '../components/plans/PlanContent';
import PlanUsers from '../components/plans/PlanUsers';
import PlanMedia from '../components/plans/PlanMedia';
import './PlanDetail.css';

function PlanDetail() {
	const { planId } = useParams();
	const { orgData } = useAuth();
	const location = useLocation();
	const [plan, setPlan] = useState(null);
	const [loading, setLoading] = useState(true);
	const [isEditMode, setIsEditMode] = useState(false);
	const [isDirty, setIsDirty] = useState(false);
	const teamMembers = location.state?.teamMembers || [];
	const roles = location.state?.roles || [];

	// Fetch initial plan data
	useEffect(() => {
		const fetchPlanDetails = async () => {
			try {
				const response = await plansApi.getOne(planId);
				setPlan(response.plan);
			} catch (error) {
				console.error('Error fetching plan:', error);
				message.error('Failed to load plan');
			} finally {
				setLoading(false);
			}
		};

		if (planId) {
			fetchPlanDetails();
		}
	}, [planId]);

	// Handle plan updates
	const handlePlanUpdate = (updatedPlan) => {
		// Preserve team member deletions by comparing the current plan's team members
		// with the updated plan's team members
		if (plan && plan.teamMembers && updatedPlan.teamMembers) {
			// If the updated plan has fewer team members, it means some were deleted
			// We should preserve this deletion
			const currentTeamMemberIds = new Set(
				plan.teamMembers.map((member) => member.id)
			);
			const updatedTeamMemberIds = new Set(
				updatedPlan.teamMembers.map((member) => member.id)
			);

			// Find deleted members (in current but not in updated)
			const deletedMemberIds = [...currentTeamMemberIds].filter(
				(id) => !updatedTeamMemberIds.has(id)
			);

			// If there are deleted members and the updated plan has a different set of team members
			if (
				deletedMemberIds.length > 0 &&
				!updatedPlan.hasOwnProperty('teamMembers') &&
				plan.teamMembers.length !== updatedPlan.teamMembers?.length
			) {
				// Filter out the deleted members from the updated plan
				updatedPlan.teamMembers = (
					updatedPlan.teamMembers || [...plan.teamMembers]
				).filter((member) => !deletedMemberIds.includes(member.id));
			}
		}

		setPlan(updatedPlan);
		setIsDirty(true);
	};

	// Add this new function to create a block
	const createBlock = async (sectionId, blockData) => {
		try {
			const response = await plansApi.createBlock(sectionId, blockData);
			return response.block;
		} catch (error) {
			console.error('Error creating block:', error);
			throw error;
		}
	};

	// Save changes
	const handleSave = async () => {
		try {
			setLoading(true);
			const organizationId = orgData.id;

			if (!organizationId) {
				throw new Error('No valid organization ID found');
			}

			// 1. Update existing data first
			const existingSections = plan.sections.filter(
				(section) => !section.id.toString().startsWith('temp_')
			);

			for (const section of existingSections) {
				// Update section
				const updatedSection = await plansApi.updateSection(
					section.id,
					{
						title: section.title,
						description: section.description,
						estimatedDuration:
							Number(section.estimatedDuration) || 0,
						order: Number(section.order),
					}
				);

				// Update existing blocks in this section
				const existingBlocks = section.blocks.filter(
					(block) => !block.id.toString().startsWith('temp_')
				);

				for (const block of existingBlocks) {
					await plansApi.updateBlock(block.id, {
						type: block.type,
						order: Number(block.order),
						title: block.title,
						content: block.content || '',
						notes: block.notes || '',
						estimatedDuration: Number(block.estimatedDuration) || 0,
						...(block.type === 'worship'
							? {
									songName: block.songName || '',
									songKey: block.songKey || '',
							  }
							: {}),
					});

					// Update block media if it has any
					if (block.media?.length >= 0) {
						await plansApi.updateBlockMedia(block.id, {
							mediaIds: block.media.map((m) => m.id),
						});
					}
				}
			}

			// 2. Create new sections and track their ID mappings
			const newSections = plan.sections.filter((section) =>
				section.id.toString().startsWith('temp_')
			);

			const sectionIdMap = new Map();

			for (const section of newSections) {
				const response = await plansApi.createSection(plan.id, {
					title: section.title,
					description: section.description,
					estimatedDuration: Number(section.estimatedDuration) || 0,
					order: Number(section.order),
				});
				sectionIdMap.set(section.id, response.section.id);
			}

			// 3. Create new blocks for each new section
			for (const section of newSections) {
				const realSectionId = sectionIdMap.get(section.id);
				const newBlocks = section.blocks.filter((block) =>
					block.id.toString().startsWith('temp_')
				);

				// Create blocks sequentially to maintain order
				for (const block of newBlocks) {
					const response = await plansApi.createBlock(realSectionId, {
						type: block.type,
						order: Number(block.order),
						title: block.title,
						content: block.content || '',
						notes: block.notes || '',
						estimatedDuration: Number(block.estimatedDuration) || 0,
						...(block.type === 'worship'
							? {
									songName: block.songName || '',
									songKey: block.songKey || '',
							  }
							: {}),
					});

					// 4. Add media to the new block if it has any
					if (block.media?.length > 0) {
						await plansApi.updateBlockMedia(response.block.id, {
							mediaIds: block.media.map((m) => m.id),
						});
					}
				}
			}

			// Fetch the final updated plan
			const response = await plansApi.getOne(plan.id);
			setPlan(response.plan);
			setIsDirty(false);
			setIsEditMode(false);
			message.success('Changes saved successfully');
		} catch (error) {
			console.error('Error saving changes:', error);
			message.error(`Failed to save changes: ${error.message}`);
		} finally {
			setLoading(false);
		}
	};

	// Add this new function to handle block deletion
	const handleDeleteBlock = async (blockId, sectionId) => {
		try {
			setLoading(true);
			await plansApi.deleteBlock(blockId);

			// Preserve the current team members when fetching the updated plan
			const currentTeamMembers = plan.teamMembers || [];
			const currentTeamMemberIds = new Set(
				currentTeamMembers.map((member) => member.id)
			);

			// Fetch the updated plan data
			const response = await plansApi.getOne(planId);

			// Ensure we preserve any team member deletions
			if (response.plan.teamMembers) {
				response.plan.teamMembers = response.plan.teamMembers.filter(
					(member) => currentTeamMemberIds.has(member.id)
				);
			}

			setPlan(response.plan);
			message.success('Block deleted successfully');
		} catch (error) {
			console.error('Error deleting block:', error);
			message.error('Failed to delete block');
		} finally {
			setLoading(false);
		}
	};

	if (loading) {
		return (
			<DashboardLayout>
				<div className='plan-detail-page'>Loading...</div>
			</DashboardLayout>
		);
	}

	if (!plan) {
		return (
			<DashboardLayout>
				<div className='plan-detail-page'>Plan not found</div>
			</DashboardLayout>
		);
	}

	return (
		<DashboardLayout>
			<div className='plan-detail-page'>
				<PlanControls
					plan={plan}
					orgData={orgData}
					onPlanUpdate={handlePlanUpdate}
					isEditMode={isEditMode}
					setIsEditMode={setIsEditMode}
					onSave={handleSave}
					isDirty={isDirty}
				/>

				<div className='plan-detail-content'>
					<div className='plan-main-content'>
						<PlanContent
							plan={plan}
							orgData={orgData}
							onPlanUpdate={handlePlanUpdate}
							isEditMode={isEditMode}
							onDeleteBlock={handleDeleteBlock}
						/>
					</div>

					<div className='plan-sidebar'>
						<PlanUsers
							plan={plan}
							orgData={orgData}
							onPlanUpdate={handlePlanUpdate}
							members={teamMembers}
							roles={roles}
						/>
						<PlanMedia
							plan={plan}
							orgData={orgData}
							onPlanUpdate={handlePlanUpdate}
						/>
					</div>
				</div>
			</div>
		</DashboardLayout>
	);
}

export default PlanDetail;
